import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Box, Button, HStack, Input, Stack, Switch, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import { flushLogSink, saveLogSinkSettings } from '../diagnostics/diagnostics_setup.ts';
import { appLog, type LogEntry } from '../diagnostics/log.ts';
import { readLogSinkSettings } from '../diagnostics/log_sink.ts';
import { SettingsGroup, SettingsRow } from './settings_ui.tsx';

// The diagnostics part of the settings: sending the log to an address (off by default), and the
// newest entries of the log, in the settings' rows (look LK-8).

const SHOWN_ENTRIES = 50;
const LEVEL_COLORS: Readonly<Record<LogEntry['level'], string>> = { info: 'fg.muted', warn: 'orange.400', error: 'red.400' };

function time(entry: LogEntry) : string {
    return new Date(entry.time).toLocaleTimeString(undefined, { hour12: false });
}

function RecentLog() : ReactNode {
    const entries = useSyncExternalStore(appLog.subscribe, appLog.entries);
    // `entries` is the same array as it grows; its length tells React that it changed.
    const count = useSyncExternalStore(appLog.subscribe, () => appLog.entries().length);
    const shown = entries.slice(Math.max(0, count - SHOWN_ENTRIES)).reverse();

    return (
        <Box px="4" py="3" maxH="320px" overflowY="auto" fontFamily="mono" fontSize="xs">
            {shown.length === 0 ? <Text color="fg.muted">Nothing logged yet.</Text> : null}
            {shown.map((entry) => (
                <Text key={entry.seq} color={LEVEL_COLORS[entry.level]} whiteSpace="pre-wrap" overflowWrap="anywhere">
                    {time(entry)} {entry.message}
                </Text>
            ))}
        </Box>
    );
}

export function LogSettings({ shell }: { shell: Bridge }) : ReactNode {
    const [url, setUrl] = useState('');
    const [savedUrl, setSavedUrl] = useState('');
    const [enabled, setEnabled] = useState(false);
    const [message, setMessage] = useState<string | undefined>();

    useEffect(() => {
        void readLogSinkSettings(shell).then((settings) => {
            setUrl(settings.url);
            setSavedUrl(settings.url);
            setEnabled(settings.enabled);
        });
    }, [shell]);

    const save = async (next: { url: string; enabled: boolean }) : Promise<void> => {
        await saveLogSinkSettings(shell, next);
        setSavedUrl(next.url);
        setUrl(next.url);
        setEnabled(next.enabled);
        setMessage(next.enabled && next.url !== '' ? `The log is sent to ${next.url}.` : 'The log is not sent.');
    };

    const sendNow = async () : Promise<void> => {
        try {
            await flushLogSink();
            setMessage('Sent.');
        } catch (error) {
            setMessage(`Not sent: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    return (
        <Stack gap="6">
            <SettingsGroup title="Sending the log" note={message ?? 'Off unless you turn it on. Passwords and tokens from the dashboard are masked before anything is sent.'}>
                <SettingsRow
                    icon="file-lines"
                    title="Send the log"
                    detail={enabled && savedUrl !== '' ? savedUrl : 'To an address of your choice'}
                    end={
                        <Switch.Root checked={enabled} onCheckedChange={(details) => void save({ url: url.trim(), enabled: details.checked })} aria-label="Send the log">
                            <Switch.HiddenInput data-testid="log-sink-enabled" />
                            <Switch.Control />
                        </Switch.Root>
                    }
                />
                <HStack gap="3" px="4" py="3">
                    <Input
                        aria-label="Address"
                        type="url"
                        size="sm"
                        placeholder="https://logs.example.com/casa"
                        value={url}
                        data-testid="log-sink-url"
                        onChange={(event) => setUrl(event.target.value)}
                    />
                    {url.trim() !== savedUrl ? (
                        <Button size="sm" colorPalette="blue" onClick={() => void save({ url: url.trim(), enabled })}>
                            Save
                        </Button>
                    ) : null}
                </HStack>
                <SettingsRow icon="cloud-arrow-up" title="Send now" opens={false} onClick={() => void sendNow()} />
            </SettingsGroup>
            <SettingsGroup title="The newest log entries">
                <RecentLog />
            </SettingsGroup>
        </Stack>
    );
}
